import React, { createContext, useContext, useState, useCallback } from 'react';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = useCallback((notification) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      timestamp: new Date(),
      ...notification,
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto-remove notification after duration (default 5 seconds)
    const duration = notification.duration || 5000;
    if (duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }

    return id;
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const updateNotification = useCallback((id, updates) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === id 
          ? { ...notification, ...updates }
          : notification
      )
    );
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Specific notification types
  const showProcessingNotification = useCallback((title, message) => {
    return addNotification({
      type: 'processing',
      title,
      message,
      duration: 0, // Don't auto-remove processing notifications
      icon: '⏳',
      color: 'yellow'
    });
  }, [addNotification]);

  const showSuccessNotification = useCallback((title, message) => {
    return addNotification({
      type: 'success',
      title,
      message,
      duration: 5000,
      icon: '✅',
      color: 'green'
    });
  }, [addNotification]);

  const showErrorNotification = useCallback((title, message) => {
    return addNotification({
      type: 'error',
      title,
      message,
      duration: 7000,
      icon: '❌',
      color: 'red'
    });
  }, [addNotification]);

  const showInfoNotification = useCallback((title, message) => {
    return addNotification({
      type: 'info',
      title,
      message,
      duration: 4000,
      icon: 'ℹ️',
      color: 'blue'
    });
  }, [addNotification]);

  const value = {
    notifications,
    addNotification,
    removeNotification,
    updateNotification,
    clearAllNotifications,
    showProcessingNotification,
    showSuccessNotification,
    showErrorNotification,
    showInfoNotification,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
